/**
 * Parcel → grid/county resolution.
 *
 * Pure module, no React and no network. Takes a parcel polygon — a Regrid `.json` payload,
 * a GeoJSON Feature/FeatureCollection, or a bare geometry — and answers the three questions
 * the PRF model needs about a specific deed:
 *
 *   · which RMA grid(s) does it touch, and how much of it sits in each;
 *   · which county, and therefore which County Base Value applies;
 *   · does it cross a grid line, which is what makes the contiguous-acreage ELECTION
 *     (handbook FCIC-18150 para 74/76) available to it.
 *
 * ⚠️ Grid membership is a real polygon test, not a bounding-box test. `collect/regrid_parcels.py`
 * uses the bbox, which over-reports: an L-shaped or diagonal deed whose bbox clips a corner of a
 * neighboring cell is reported as spanning it. An election is an insurance filing, so a false
 * positive there is not cosmetic.
 *
 * Regrid note: the public parcel endpoint returns `fields: {}` for anonymous callers — no owner,
 * acreage, land use or valuation. `geometry` IS returned in full, and everything this module
 * computes comes from the polygon, so the gated attributes cost nothing here. Acreage is measured
 * off the polygon rather than read from a field.
 */

// Mirrors ground-score-pipeline/collect/gridgeo.py — decode() returns the cell CENTER,
// so a cell spans center ± CELL/2.
const CELL = 0.25;
const LAT0 = 20.125;
const LON0 = -130.125;
const STRIDE = 300;

export const gridOf = (lon, lat) =>
  Math.round((lat - LAT0) / CELL) * STRIDE + Math.round((lon - LON0) / CELL);

export const gridCentre = (gid) => {
  const row = Math.floor(gid / STRIDE);
  const col = gid - row * STRIDE;
  return { lat: LAT0 + CELL * row, lon: LON0 + CELL * col };
};

export const gridBounds = (gid) => {
  const { lat, lon } = gridCentre(gid);
  return {
    lat0: lat - CELL / 2,
    lat1: lat + CELL / 2,
    lon0: lon - CELL / 2,
    lon1: lon + CELL / 2,
  };
};

/* ── geometry helpers ──────────────────────────────────────────────────── */

// Every ring in the geometry, as flat [lon, lat] arrays. Polygon and MultiPolygon both
// reduce to a list of rings; holes are included, which is what the area calculation wants.
const ringsOf = (geom) => {
  if (!geom) return [];
  if (geom.type === "Polygon") return geom.coordinates;
  if (geom.type === "MultiPolygon") return geom.coordinates.flat();
  return [];
};

// Outer rings only — used for point-in-polygon, where holes are handled by the even-odd rule
// falling out of testing every ring together.
const allRings = (geom) => ringsOf(geom);

export const bbox = (geom) => {
  let lat0 = Infinity,
    lat1 = -Infinity,
    lon0 = Infinity,
    lon1 = -Infinity;
  for (const ring of ringsOf(geom)) {
    for (const [x, y] of ring) {
      if (y < lat0) lat0 = y;
      if (y > lat1) lat1 = y;
      if (x < lon0) lon0 = x;
      if (x > lon1) lon1 = x;
    }
  }
  return { lat0, lat1, lon0, lon1 };
};

// Even-odd ray casting across every ring at once, so interior holes correctly read as outside.
const pointInPolygon = (geom, x, y) => {
  let inside = false;
  for (const ring of allRings(geom)) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      const straddles = (yi > y) !== (yj > y);
      if (straddles && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
        inside = !inside;
    }
  }
  return inside;
};

/**
 * Every grid cell an edge passes through, by walking the cell lattice along the segment.
 * Sampling the segment at sub-cell resolution is exact enough here: a cell is ~17 miles and
 * the step is a twentieth of a cell, so no crossing can be skipped.
 */
const cellsAlongEdge = (x0, y0, x1, y1, out) => {
  const steps = Math.max(
    1,
    Math.ceil((Math.abs(x1 - x0) + Math.abs(y1 - y0)) / (CELL / 20)),
  );
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    out.add(gridOf(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t));
  }
};

/**
 * The RMA grids a parcel actually occupies.
 *
 * Three sources, because none alone is complete:
 *   1. the cell each vertex falls in;
 *   2. the cells each edge crosses — catches a boundary that clips a corner between vertices;
 *   3. any bbox cell whose center is inside the polygon — catches cells swallowed whole by a
 *      parcel larger than a cell (rare, but a 214-sq-mi cell is not large for timber ground).
 */
export const gridsTouched = (geom) => {
  const cells = new Set();
  for (const ring of ringsOf(geom)) {
    for (let i = 0; i < ring.length; i++) {
      const [x0, y0] = ring[i];
      const [x1, y1] = ring[(i + 1) % ring.length];
      cells.add(gridOf(x0, y0));
      cellsAlongEdge(x0, y0, x1, y1, cells);
    }
  }
  const b = bbox(geom);
  if (Number.isFinite(b.lat0)) {
    const r0 = Math.round((b.lat0 - LAT0) / CELL);
    const r1 = Math.round((b.lat1 - LAT0) / CELL);
    const c0 = Math.round((b.lon0 - LON0) / CELL);
    const c1 = Math.round((b.lon1 - LON0) / CELL);
    for (let r = Math.min(r0, r1); r <= Math.max(r0, r1); r++) {
      for (let c = Math.min(c0, c1); c <= Math.max(c0, c1); c++) {
        const gid = r * STRIDE + c;
        if (cells.has(gid)) continue;
        const { lat, lon } = gridCentre(gid);
        if (pointInPolygon(geom, lon, lat)) cells.add(gid);
      }
    }
  }
  return [...cells].sort((a, b2) => a - b2);
};

const ringSqMi = (ring, kx, ky) => {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x0, y0] = ring[i];
    const [x1, y1] = ring[(i + 1) % ring.length];
    s += x0 * kx * (y1 * ky) - x1 * kx * (y0 * ky);
  }
  return Math.abs(s / 2);
};

/**
 * Shoelace on a local equirectangular projection. Good to a fraction of a percent at parcel
 * scale, which is well inside the precision of the source geometry.
 *
 * ⚠️ Each ring is taken as |area| and combined explicitly — outer ring minus its holes, summed
 * across the parts of a MultiPolygon. Summing SIGNED ring areas globally looks equivalent and
 * is not: real parcel data does not wind consistently, so two parts of a MultiPolygon can
 * cancel. That understated a 6,750-acre timber tract as 626 acres.
 */
export const acresOf = (geom) => {
  const b = bbox(geom);
  if (!Number.isFinite(b.lat0)) return 0;
  const midRad = (((b.lat0 + b.lat1) / 2) * Math.PI) / 180;
  const kx = 69.17 * Math.cos(midRad);
  const ky = 69.05;
  const polys =
    geom.type === "MultiPolygon"
      ? geom.coordinates
      : geom.type === "Polygon"
        ? [geom.coordinates]
        : [];
  let sqMi = 0;
  for (const poly of polys) {
    if (!poly.length) continue;
    sqMi += ringSqMi(poly[0], kx, ky);
    for (let i = 1; i < poly.length; i++) sqMi -= ringSqMi(poly[i], kx, ky);
  }
  return Math.max(0, sqMi) * 640;
};

/**
 * Share of the parcel falling in each touched grid, by sampling the bounding box on a lattice.
 * Approximate by construction — it exists to pick the PRIMARY grid and to show roughly how the
 * deed straddles, not to compute a premium. The elected grid, which is what the money turns on,
 * does not depend on these shares at all.
 */
export const gridShares = (geom, grids) => {
  if (grids.length <= 1) return grids.length ? { [grids[0]]: 1 } : {};
  const b = bbox(geom);
  const N = 60;
  const counts = {};
  let hits = 0;
  for (let i = 0; i < N; i++) {
    const y = b.lat0 + ((b.lat1 - b.lat0) * (i + 0.5)) / N;
    for (let j = 0; j < N; j++) {
      const x = b.lon0 + ((b.lon1 - b.lon0) * (j + 0.5)) / N;
      if (!pointInPolygon(geom, x, y)) continue;
      hits++;
      const g = gridOf(x, y);
      counts[g] = (counts[g] || 0) + 1;
    }
  }
  if (!hits) return { [grids[0]]: 1 };
  const out = {};
  for (const g of Object.keys(counts)) out[g] = counts[g] / hits;
  return out;
};

/* ── input parsing ─────────────────────────────────────────────────────── */

/**
 * Accepts what a user can actually get hold of:
 *   · a Regrid parcel `.json` payload (has `geometry`, `centroid`, `spec.geoid`, `headline`);
 *   · a GeoJSON Feature or FeatureCollection;
 *   · a bare Polygon/MultiPolygon geometry.
 *
 * Returns null rather than throwing on anything unrecognised — the caller surfaces that as a
 * parse error, which is more useful than a stack trace.
 */
export const parseParcel = (input) => {
  let j = input;
  if (typeof input === "string") {
    try {
      j = JSON.parse(input);
    } catch {
      return null;
    }
  }
  if (!j || typeof j !== "object") return null;

  let geom = null;
  let props = {};
  let label = null;
  let geoid = null;

  if (j.type === "FeatureCollection") {
    const f = (j.features || [])[0];
    if (!f) return null;
    geom = f.geometry;
    props = f.properties || {};
  } else if (j.type === "Feature") {
    geom = j.geometry;
    props = j.properties || {};
  } else if (j.type === "Polygon" || j.type === "MultiPolygon") {
    geom = j;
  } else if (j.geometry) {
    // Regrid shape: `fields` carries the assessor record, `spec.geoid` the county FIPS.
    // A rate-limited response (HTTP 429) still returns geometry but an EMPTY `fields`, so
    // every read below degrades to null rather than assuming the record is populated.
    geom = j.geometry;
    props = j.fields || {};
    label = j.headline || null;
    geoid = j.spec?.geoid || props.geoid || null;
  } else {
    return null;
  }

  if (!geom || !ringsOf(geom).length) return null;

  const grids = gridsTouched(geom);
  const acres = acresOf(geom);
  const shares = gridShares(geom, grids);
  // Primary = the grid holding the most of the deed. This is the grid the app selects, and
  // the one a policy defaults to before any election is considered.
  const primary =
    grids.length === 1
      ? grids[0]
      : Number(
          Object.entries(shares).sort((a, b) => b[1] - a[1])[0]?.[0] ?? grids[0],
        );

  const b = bbox(geom);
  const centroid = j.centroid || [
    (b.lon0 + b.lon1) / 2,
    (b.lat0 + b.lat1) / 2,
  ];

  const num = (v) => {
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isFinite(n) ? n : null;
  };

  // The assessor's mapped acreage is authoritative where present; the shoelace figure is the
  // fallback. They disagree by a few percent on real deeds — the polygon may include right of
  // way the assessor excludes — so prefer the record and keep the computed value visible.
  const mapAcres = num(props.mapacr ?? props.ll_gisacre ?? props.gisacre);

  return {
    label: label || props.address || props.SITUS_ADDRESS || null,
    geoid: geoid || props.FIPS_NR || null,
    parcelNumber: props.parcelnumb || props.PARCEL_ID_NR || null,
    countyName: props.county || props.COUNTY_NM || null,
    state: props.state2 || null,
    zip: props.szip5 || props.szip || null,
    owner: props.owner || null,
    useCode: props.usecode ?? null,
    useDesc: props.usedesc || props.zoning_description || null,
    zoning: props.zoning || null,
    landValue: num(props.landval ?? props.mlnd ?? props.VALUE_LAND),
    improvementValue: num(props.improvval ?? props.mimp ?? props.VALUE_BLDG),
    geometry: geom,
    bbox: b,
    centroid: { lon: centroid[0], lat: centroid[1] },
    acres: mapAcres ?? acres,
    acresComputed: acres,
    acresSource: mapAcres != null ? "assessor" : "computed",
    grids,
    shares,
    primary,
    spansGridLine: grids.length > 1,
  };
};

/**
 * What the election is worth to THIS deed.
 *
 * The grid card's `eu` is a potential — the best rate reachable from any of the eight
 * neighbors. A real parcel only reaches the grids it physically touches, so the realized
 * value is computed against that set and is often much smaller. `sharedByGid` maps grid id
 * to the shared grid record; `segRecord` supplies the CBV for the intended use in view.
 */
export const electionValue = (parcel, sharedByGid, segRecord, subsidyByCl) => {
  if (!parcel || parcel.grids.length < 2 || !segRecord) return null;
  const opts = parcel.grids
    .map((g) => sharedByGid[String(g)])
    .filter(Boolean)
    .map((s) => ({ gid: s.g, rate: s.er, cl: s.cl }));
  if (opts.length < 2) return null;

  const base =
    opts.find((o) => Number(o.gid) === parcel.primary) ||
    opts.reduce((a, b2) => (b2.rate < a.rate ? b2 : a));
  const best = opts.reduce((a, b2) => (b2.rate > a.rate ? b2 : a));
  if (best.rate <= base.rate) return { base, best, perAcre: 0, annual: 0, opts };

  const sub = subsidyByCl?.[best.cl] ?? 0.51;
  const perAcre = segRecord.b * 1.5 * best.cl * (best.rate - base.rate) * sub;
  return { base, best, perAcre, annual: perAcre * parcel.acres, opts };
};
